import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import OrderScreen from './screens/OrderScreen';
import HistoryScreen from './screens/HistoryScreen';
import MenuEditor from './screens/MenuEditor';
import Analytics from './screens/Analytics';
import LoginScreen from './screens/LoginScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import SettingsScreen from './screens/SettingsScreen';
import DayCloseScreen from './screens/DayCloseScreen';
import CarnetScreen from './screens/CarnetScreen';
import ReservationsScreen from './screens/ReservationsScreen';
import LegalScreen from './screens/LegalScreen';
import Navbar from './components/Navbar';
import { LanguageProvider, useLang } from './utils/LanguageContext';
import { AuthProvider, useAuth } from './utils/AuthContext';
import { t } from './utils/translations';
import './App.css';
import Loader from './components/Loader';

// The screen area — keyed by path so each navigation gently fades in.
function AppRoutes({ canManage, isPro, isEvents }) {
  const location = useLocation();

  // An events branch (wedding hall) has its own module: Réservations, not POS.
  if (isEvents) {
    return (
      <div className="content page-fade" key={location.pathname}>
        <Routes location={location}>
          <Route path="/" element={<ReservationsScreen />} />
          <Route path="/settings" element={canManage ? <SettingsScreen /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="content page-fade" key={location.pathname}>
      <Routes location={location}>
        <Route path="/" element={<OrderScreen />} />
        <Route path="/history" element={<HistoryScreen />} />
        <Route path="/carnet" element={isPro ? <CarnetScreen /> : <Navigate to="/" replace />} />
        <Route path="/menu" element={canManage ? <MenuEditor /> : <Navigate to="/" replace />} />
        <Route path="/analytics" element={canManage ? <Analytics /> : <Navigate to="/" replace />} />
        <Route path="/dayclose" element={canManage ? <DayCloseScreen /> : <Navigate to="/" replace />} />
        <Route path="/settings" element={canManage ? <SettingsScreen /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function SubscriptionLock() {
  const { org, signOut } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-icon">⏸️</div>
        <h1 className="login-title">{org?.name}</h1>
        <p className="login-sub">{tr.subExpired}</p>
        <p className="login-error">{tr.subExpiredMsg}</p>
        <button className="login-btn" onClick={signOut}>{tr.signIn}</button>
      </div>
    </div>
  );
}

function AppContent() {
  const { session, profile, org, loading, role, recovery, isPro, isEvents } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];

  // Public page — readable without an account (privacy policy + terms)
  if (typeof window !== 'undefined' && window.location.pathname === '/legal') {
    return <LegalScreen />;
  }

  if (loading) {
    return <Loader text={tr.loadingApp} />;
  }

  if (!session) {
    return <LoginScreen />;
  }

  // Arrived via a password-reset email: ask for the new password first
  if (recovery) {
    return <ResetPasswordScreen />;
  }

  // Logged in but not yet part of a café: create one or join with a code
  if (!profile) {
    return <OnboardingScreen />;
  }

  // Subscription check (manual-activation billing)
  const daysLeft = org?.paid_until
    ? Math.ceil((new Date(org.paid_until) - Date.now()) / 86400000)
    : null;

  if (daysLeft !== null && daysLeft <= 0) {
    return <SubscriptionLock />;
  }

  const canManage = role === 'owner' || role === 'manager';

  return (
    <Router>
      <div className="app">
        <Navbar />
        {daysLeft !== null && daysLeft <= 7 && (
          <div className="sub-warning">
            ⚠️ {tr.subWarning.replace('{days}', daysLeft)}
          </div>
        )}
        <AppRoutes canManage={canManage} isPro={isPro} isEvents={isEvents} />
      </div>
    </Router>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
